import { GetServerSideProps, InferGetServerSidePropsType } from "next";
import React from "react";
import { Descendant } from "slate";

import { Page } from "@/content-V2/components/Page";
import { VideoData } from "@/content-V2/components/StreamingVideo";
import type { ContentPage as ContentPageProps } from "@/content-V2/types";
import { ExperienceProvider } from "@/context/ExperienceContext";
import { Experience, getExperience } from "@/utils/experience";
import { getIsProduction } from "@/utils/production";
import { fetchProfileText, getProfileTextIdsAndRemoveUndefinedValues } from "@/utils/profileText";
import { getVideoDataMap } from "@/utils/videoData";

export const getServerSideProps: GetServerSideProps<{
  content: ContentPageProps | null;
  experience: Experience | null;
  isProduction: boolean;
  textBlocks: Record<string, Descendant[]> | null;
  videoData: Record<string, VideoData> | null;
}> = async ({ req }) => {
  const experience = getExperience(req);
  const isProduction = getIsProduction();
  const profile = experience?.profile;
  let content: ContentPageProps | undefined;
  let textBlocks: Record<string, Descendant[]> | null;
  let videoData: Record<string, VideoData> | null = null;

  try {
    const profileModule = profile
      ? await import(`../../content-V2/profiles${profile}`)
      : Promise.resolve(undefined);
    const ids: string[] = [];
    content = getProfileTextIdsAndRemoveUndefinedValues(profileModule.default, ids);

    // Fetch video data for any streaming videos in the content
    if (content && content.children) {
      videoData = await getVideoDataMap(content.children);
    }

    textBlocks = await (profile
      ? fetchProfileText(experience.country, experience.language, ids).catch(() => null)
      : Promise.resolve(null));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("Invalid profile path:", e);
    return {
      notFound: true
    };
  }

  if (
    !content ||
    (isProduction && !experience?.isProduction) ||
    (isProduction &&
      !!experience?.requiresReferer.length &&
      !experience?.requiresReferer.find((referer) => req.headers.referer?.includes(referer)))
  ) {
    return {
      props: getProfileTextIdsAndRemoveUndefinedValues(
        {
          content: null,
          experience,
          isProduction,
          textBlocks: null,
          videoData: null
        },
        []
      )
    };
  }

  return {
    props: getProfileTextIdsAndRemoveUndefinedValues(
      {
        content,
        experience,
        isProduction,
        textBlocks,
        videoData
      },
      []
    )
  };
};

const NextJsContentPage = ({
  content,
  experience,
  textBlocks,
  videoData
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  return (
    <ExperienceProvider experience={experience || {}}>
      <Page content={content} textBlocks={textBlocks} videoData={videoData} />
    </ExperienceProvider>
  );
};

export default NextJsContentPage;
